import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import PortalAlert from "./PortalAlert";
import PortalResultsHead from "./PortalResultsHead";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "./PortalFiltersCollapsible";
import "../assets/css/DigixLeads.css";
import PortalSelect from "./PortalSelect";
import { formatBusinessName } from "../utils/formatters";

const PORTAL_SERVICES_ROWS_PER_PAGE_KEY = "portalServicesRowsPerPage";
const PORTAL_SERVICES_CURRENT_PAGE_KEY = "portalServicesCurrentPage";

const SERVICE_STATUSES = ["Active", "Inactive"];

const EMPTY_FORM = {
  clientId: "",
  serviceName: "",
  description: "",
  status: "Active",
};

const COLUMNS = [
  { key: "serviceName", label: "Service Name" },
  { key: "clientName", label: "Client Name" },
  { key: "description", label: "Description" },
  { key: "status", label: "Status" },
];

const SORT_ACCESSORS = {
  serviceName: (s) => (s.Service_Name || "").toLowerCase(),
  clientName: (s) => (s.Client_Name || "").toLowerCase(),
  description: (s) => (s.Description || "").toLowerCase(),
  status: (s) => (s.Status || "").toLowerCase(),
};

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function isServiceClient(client) {
  if (String(client?.Status || "").toLowerCase() === "inactive") return false;
  const sub = String(client?.Business_Sub_Type || "").trim();
  return !sub || sub === "Service" || sub === "Both";
}

function TenantServices() {
  const navigate = useNavigate();
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [resultKeyword, setResultKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [sortKey, setSortKey] = useState("serviceName");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_SERVICES_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_SERVICES_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return 10;
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const toggleSort = (key) => {
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(PORTAL_SERVICES_ROWS_PER_PAGE_KEY, String(next));
  };

  const loadServices = useCallback(async () => {
    const res = await axiosInstance.get("/client/tenant-services");
    setServices(Array.isArray(res.data?.data) ? res.data.data : []);
  }, []);

  const loadClients = useCallback(async () => {
    const res = await axiosInstance.get("/client/tenant-clients");
    const rows = Array.isArray(res.data?.data) ? res.data.data : [];
    setClients(rows.filter(isServiceClient));
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await Promise.all([loadServices(), loadClients()]);
      } catch (_) {
        if (active) setError("Could not load services.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadServices, loadClients]);

  useEffect(() => {
    localStorage.setItem(PORTAL_SERVICES_CURRENT_PAGE_KEY, String(currentPage));
  }, [currentPage]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return services.filter((s) => {
      if (statusFilter && String(s.Status || "") !== statusFilter) return false;
      if (clientFilter && String(s.Tenant_Client_Id) !== String(clientFilter)) return false;
      if (!q) return true;
      const haystack = [s.Service_Name, s.Client_Name, s.Description, s.Status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [services, search, statusFilter, clientFilter]);

  const resultFiltered = useMemo(() => {
    const q = resultKeyword.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((s) =>
      [s.Service_Name, s.Client_Name, s.Description, s.Status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [filtered, resultKeyword]);

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey] || SORT_ACCESSORS.serviceName;
    const next = [...resultFiltered];
    next.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return next;
  }, [resultFiltered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage) || 1);
  const page = Math.min(currentPage, totalPages);
  const pageRows = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const skipFilterPageResetRef = useRef(true);
  useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [search, resultKeyword, statusFilter, clientFilter]);

  const hasActiveFilters = Boolean(search || statusFilter || clientFilter);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setClientFilter("");
    setCurrentPage(1);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientId) {
      setFormError("Client name is required.");
      return;
    }
    if (!form.serviceName.trim()) {
      setFormError("Service name is required.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post(`/client/tenant-clients/${form.clientId}/services`, {
        Service_Name: form.serviceName.trim(),
        Description: form.description.trim() || null,
        Status: form.status || "Active",
      });
      await loadServices();
      closeAddModal();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create service."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page">
      <div className="cportal-page-head">
        <h1>Services</h1>
      </div>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      <PortalFiltersCollapsible
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search service, client, description"
        searchAriaLabel="Search services"
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        isCompactViewport={isCompactViewport}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        actions={
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            Add Service
          </button>
        }
      >
        <div className="cportal-filter-row">
          <select
            value={clientFilter}
            onChange={(e) => {
              setClientFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Clients</option>
            {clients.map((c) => (
              <option key={c.Tenant_Client_Id} value={c.Tenant_Client_Id}>
                {c.Client_Name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            {SERVICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </PortalFiltersCollapsible>

      <div className="cportal-card">
        <PortalResultsHead
          heading="Services"
          count={sorted.length}
          keyword={resultKeyword}
          onKeywordChange={setResultKeyword}
          ariaLabel="Keyword search services"
        />

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">
            {services.length === 0 ? "No services added yet." : "No services match your filters."}
          </p>
        ) : (
          <>
          <div className="cportal-table-wrap co-desktop-table">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((service) => (
                  <tr key={service.Tenant_Client_Service_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() => navigate(`/portal/clients/${service.Tenant_Client_Id}`)}
                      >
                        {display(service.Service_Name)}
                      </button>
                    </td>
                    <td>{display(formatBusinessName(service.Client_Name))}</td>
                    <td>{display(service.Description)}</td>
                    <td>
                      <span className={statusBadgeClass(service.Status)}>
                        {display(service.Status)}
                      </span>
                    </td>
                    <td className="cportal-td-actions">
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--view"
                          onClick={() => navigate(`/portal/clients/${service.Tenant_Client_Id}`)}
                          title="View Client"
                          aria-label="View Client"
                        >
                          <FaEye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ResultsMobileList
            emptyLabel={
              services.length === 0 ? "No services added yet." : "No services match your filters."
            }
            className="cportal-services-mobile"
          >
            {pageRows.map((service) => {
              const id = service.Tenant_Client_Service_Id;
              const clientId = service.Tenant_Client_Id;
              const status = service.Status || "—";
              const statusTone =
                String(status).toLowerCase() === "active"
                  ? "success"
                  : String(status).toLowerCase() === "inactive"
                    ? "danger"
                    : "neutral";
              return (
                <ResultsMobileCard
                  key={id}
                  title={display(service.Service_Name)}
                  subtitle={formatBusinessName(service.Client_Name) || null}
                  meta={[{ label: status, tone: statusTone, status: true }]}
                  fields={[
                    { label: "Client", value: formatBusinessName(service.Client_Name) },
                    { label: "Description", value: service.Description },
                  ]}
                  onOpen={() => navigate(`/portal/clients/${clientId}`)}
                  actions={
                    <div className="co-mcard__action-grid">
                      <button
                        type="button"
                        className="co-maction"
                        title="View Client"
                        aria-label="View Client"
                        onClick={() => navigate(`/portal/clients/${clientId}`)}
                      >
                        <FaEye size={14} />
                        <span>View</span>
                      </button>
                    </div>
                  }
                />
              );
            })}
          </ResultsMobileList>
          </>
        )}

        {!loading && sorted.length > 0 && (
          <TablePagination
            currentPage={page}
            totalItems={sorted.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel="services"
          />
        )}
      </div>

      {showAddModal && (
        <div className="cportal-modal-overlay" onClick={closeAddModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="add-service-title"
          >
            <div className="cportal-modal-header">
              <h3 id="add-service-title">Add Service</h3>
              <button type="button" onClick={closeAddModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleSubmit}>
              <PortalAlert type="error" message={formError} onClear={() => setFormError("")} />
              <div className="cportal-form-grid">
                <div className="cportal-field cportal-field--full">
                  <label>
                    Client Name <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.clientId}
                    onChange={(e) => setField("clientId", e.target.value)}
                    required
                  >
                    <option value="">Select Client</option>
                    {clients.map((c) => (
                      <option key={c.Tenant_Client_Id} value={c.Tenant_Client_Id}>
                        {c.Client_Name}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field">
                  <label>
                    Service Name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.serviceName}
                    onChange={(e) => setField("serviceName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Status <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value)}
                  >
                    {SERVICE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
              </div>
              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeAddModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={submitting}>
                  {submitting ? "Creating…" : "Create Service"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantServices;
